'use client';

import { useState, useRef, MouseEvent, TouchEvent } from 'react';
import Image from 'next/image';
import type { Palette } from './types';

export type HeroCopy = {
  badge?: string;
  title?: string;
  highlight?: string;
  blurb?: string;
  primaryCtaText?: string;
  primaryCtaHref?: string;
  secondaryCtaText?: string;
  secondaryCtaHref?: string;
  trustPill?: string;
};

type HeroProps = { palette: Palette; copy: HeroCopy };

export function HeroSection({ palette, copy }: HeroProps) {
  // State to track the slider percentage (0 to 100)
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);

  // Logic to calculate position based on mouse/touch
  const handleMove = (clientX: number) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      // Clamp value between 0 and 100
      setSliderPosition(Math.min(Math.max(percentage, 0), 100));
    }
  };

  const onMouseMove = (e: MouseEvent<HTMLDivElement>) => handleMove(e.clientX);
  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => handleMove(e.touches[0].clientX);

  return (
    <section
      className="relative overflow-hidden px-6 py-10 sm:py-16 lg:px-8"
      style={{ 
        backgroundColor: palette.forest,
        color: palette.white 
      }}
    >
      {/* --- BACKGROUND EFFECTS --- */}
      <div 
        className="absolute inset-0 z-0 opacity-100"
        style={{ background: `linear-gradient(135deg, ${palette.forest} 0%, ${palette.sage} 100%)` }}
      />
      <div 
        className="absolute -right-20 top-0 h-[600px] w-[600px] rounded-full opacity-20 blur-[100px]"
        style={{ background: palette.clay }}
      />
      
      {/* --- CONTENT CONTAINER --- */}
      <div className="relative z-10 mx-auto grid max-w-7xl gap-10 lg:grid-cols-2 lg:items-center">
        
        {/* TEXT COLUMN (Left) */}
        <div className="space-y-6">
          <div
            className="inline-flex items-center gap-2 rounded-full border border-amber-200/20 bg-white/5 px-3 py-1.5 backdrop-blur-sm"
          >
            <span className="flex h-1.5 w-1.5 rounded-full bg-amber-400 shadow-[0_0_8px_#fbbf24]"></span>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-100">
              {copy.badge || 'Your Vision, Our Expertise'}
            </span>
          </div>
          
          <h1 className="text-4xl font-bold leading-tight tracking-tight drop-shadow-lg sm:text-5xl lg:text-6xl text-white">
            {(copy.title || 'Make reality match').replace(/<br\s*\/?>/gi, '')} <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-emerald-200">
              {copy.highlight || 'your vision.'}
            </span>
          </h1>
          
          <p className="text-lg leading-normal text-emerald-50/90 max-w-lg">
            {copy.blurb ||
              (
                <>
                  Bridging the gap between the house you have and the home you want. 
                  We coordinate <span className="font-semibold text-white">roofing, water, and lighting</span> upgrades so the results 
                  feel intentional and the price makes sense.
                </>
              )}
          </p>
          
          <div className="flex flex-wrap gap-3 pt-2">
            <a
              href={copy.primaryCtaHref || '#quote'}
              className="relative overflow-hidden rounded-full px-6 py-3.5 text-sm font-bold uppercase tracking-[0.2em] text-white shadow-xl transition-all hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-amber-500/20"
              style={{ 
                background: `linear-gradient(to right, ${palette.clay}, #b45309)` 
              }}
            >
              <span className="relative z-10">{copy.primaryCtaText || 'Get a Quote'}</span>
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent hover:animate-shimmer" />
            </a>

            <a
              href={copy.secondaryCtaHref || '#services'}
              className="group rounded-full border border-white/30 bg-white/5 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm transition-all hover:bg-white/10 hover:border-white/60"
            >
              {copy.secondaryCtaText || 'Services'}
            </a>
          </div>

          <div className="flex flex-wrap gap-3 border-t border-white/10 pt-5">
            <div className="flex items-center gap-2 rounded-xl bg-black/20 px-4 py-2 backdrop-blur-md border border-white/5">
              <span className="text-sm text-amber-400">★★★★★</span>
              <span className="text-xs font-medium text-emerald-50">
                {copy.trustPill || 'Trusted coordination.'}
              </span>
            </div>
          </div>
        </div>

        {/* --- INTERACTIVE SLIDER COLUMN (Right) --- */}
        <div 
          ref={containerRef}
          onMouseMove={onMouseMove}
          onTouchMove={onTouchMove}
          className="relative h-72 w-full cursor-col-resize overflow-hidden rounded-2xl border-4 border-white/10 shadow-2xl sm:h-96 select-none touch-none"
        >
            
            {/* 1. BOTTOM LAYER: Reality (The "Before") */}
            {/* Visible on the RIGHT side of the slider line */}
            <div className="absolute inset-0 select-none">
               <Image
                 src="/home-pros/AI_images/uhouse.webp"
                 alt="Current Reality"
                 fill
                 className="object-cover grayscale brightness-75 filter contrast-125 select-none"
                 draggable={false}
               />
               <div className="absolute bottom-4 right-4 z-10 pointer-events-none">
                 <span className="rounded-full bg-black/60 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white/80 backdrop-blur-md border border-white/10">
                   Right Now
                 </span>
               </div>
            </div>

            {/* 2. TOP LAYER: Vision (The "After") */}
            {/* Visible on the LEFT side of the slider line */}
            <div 
              className="absolute inset-0 z-20 select-none"
              style={{ 
                // We crop the right side based on the slider position
                clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` 
              }}
            >
               <Image
                 src="/home-pros/AI_images/bhouse.webp"
                 alt="Future Vision"
                 fill
                 className="object-cover select-none"
                 draggable={false}
               />
               <div className="absolute top-4 left-4 pointer-events-none">
                 <span className="rounded-full bg-emerald-500/90 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow-lg backdrop-blur-md">
                   The Vision
                 </span>
               </div>
            </div>

            {/* 3. THE HANDLE LINE */}
            <div 
              className="absolute inset-y-0 z-30 w-1 bg-white shadow-[0_0_15px_rgba(255,255,255,0.5)] cursor-col-resize"
              style={{ left: `${sliderPosition}%` }}
            >
               {/* Handle Icon */}
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white flex items-center justify-center shadow-lg transform active:scale-95 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-emerald-800">
                    <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
               </div>
            </div>

        </div>

      </div>
    </section>
  );
}
